import React, { useCallback, useMemo, useState, type FC } from "react";
import { Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { useDeleteTemplate } from "../../../../queries/dynamicBonuses/useDeleteTemplate";
import { useGetTemplate } from "../../../../queries/dynamicBonuses/useGetTemplate";
import { useUpdateTemplate } from "../../../../queries/dynamicBonuses/useUpdateTemplate";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import CreateCampaignWizard from "../Campaigns/CreateCampaignWizard/CreateCampaignWizard";
import { templateToWizardValues } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import DeleteDynamicBonusModal from "../controls/DeleteDynamicBonusModal";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { templateClonePath } from "../clonePaths";
import type { TemplateIn } from "../types";
import styles from "../stylesDynamicBonuses.module.css";

type TemplateDeleteModalProps = {
  templateId: string;
  templateName: string;
  onClose: () => void;
  onDeleted: () => void;
};

const TemplateDeleteModal: FC<TemplateDeleteModalProps> = ({
  templateId,
  templateName,
  onClose,
  onDeleted,
}) => {
  const deleteMutation = useDeleteTemplate(templateId);

  return (
    <DeleteDynamicBonusModal
      open
      resourceType="template"
      resourceName={templateName}
      isPending={deleteMutation.isPending}
      onClose={onClose}
      onConfirm={() =>
        deleteMutation.mutate(undefined, { onSuccess: onDeleted })
      }
    />
  );
};

/**
 * Template editing reuses the campaign wizard in "template" mode — the same
 * source of truth as create. The template is hydrated into wizard form values
 * via the shared `templateToWizardValues`, the wizard assembles the updated
 * `TemplateIn`, and this page owns the PUT + post-success navigation back to
 * the templates list.
 */
const EditTemplatePage: FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const id = templateId ?? "";
  const { data: template, isLoading, isError } = useGetTemplate(id);
  const { mutate: updateTemplate, isPending } = useUpdateTemplate(id);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { isEnabled: duplicateBuilderEnabled } = usePamFeature(
    "dynamicBonusesDuplicateBuilder",
  );

  // The wizard applies `initialValues` once on mount, so it must not render
  // until the template has loaded. Name/description have no hydration in
  // `templateToWizardValues` (it maps the campaign-shaped fields), so add them.
  const initialValues = useMemo(
    () =>
      template
        ? {
            ...templateToWizardValues(template),
            name: template.name,
            description: template.description ?? "",
          }
        : undefined,
    [template],
  );

  const handleSubmit = useCallback(
    (payload: TemplateIn) => {
      updateTemplate(payload, {
        onSuccess: () => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATES),
      });
    },
    [updateTemplate, navigate],
  );

  if (isLoading || isError || !template) {
    return (
      <>
        <FeatureFlagGuard feature="dynamicBonuses" mode="route">
          {isLoading ? (
            <Spin data-testid="edit-template-loading" />
          ) : (
            <p className={styles.listError}>
              {AppStrings.PAM_DYNAMIC_BONUS_TEMPLATE_LOAD_ERROR}
            </p>
          )}
        </FeatureFlagGuard>
      </>
    );
  }

  return (
    <>
      <CreateCampaignWizard
        mode="template"
        title={AppStrings.PAM_DYNAMIC_BONUS_EDIT_TEMPLATE}
        submitLabel={AppStrings.PAM_DYNAMIC_BONUS_SAVE_TEMPLATE}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isSubmitting={isPending}
        onDelete={() => setIsDeleteOpen(true)}
        onDuplicate={
          duplicateBuilderEnabled
            ? () => navigate(templateClonePath(template.template_id))
            : undefined
        }
      />

      {isDeleteOpen && (
        <TemplateDeleteModal
          templateId={template.template_id}
          templateName={template.name}
          onClose={() => setIsDeleteOpen(false)}
          onDeleted={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATES)}
        />
      )}
    </>
  );
};

export default EditTemplatePage;
